/**
 * The investor's own forms: their profile, and paying them.
 *
 * Shared by Finance → Investors (the profile) and the investor's weekly
 * breakdown. The list that used to live under Remittance & Hire Purchase is
 * gone: every investor is read from Finance → Investors. What is collected
 * for an investor is a payable, never company revenue; paying them settles
 * the payable, and is never an expense.
 */

import { useState, type ReactNode } from 'react';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, type InvestorView } from '@cenox/shared';
import { Button, ErrorState, Input, Modal, Select, Textarea } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { AccountSelect } from '../money/AccountSelect';
import { useCreateInvestor, useRecordInvestorPayout, useUpdateInvestor } from './investorData';

const METHODS = PAYMENT_METHODS.filter((m) => m !== 'credit');

/**
 * Add an investor, or edit one: the same fields and the same validation.
 *
 * Editing updates the investor in place, by id — never a new record — and only
 * the profile: bikes, agreements, payment shares and payouts are untouched. If
 * saving fails the values typed stay in the form.
 */
export function InvestorFormDialog({
  investor,
  onClose,
  onSaved,
}: {
  investor?: InvestorView;
  onClose: () => void;
  onSaved?: () => void;
}): ReactNode {
  const create = useCreateInvestor();
  const update = useUpdateInvestor();
  const [name, setName] = useState(investor?.name ?? '');
  const [reference, setReference] = useState(investor?.reference ?? '');
  const [phone, setPhone] = useState(investor?.phone ?? '');
  const [email, setEmail] = useState(investor?.email ?? '');
  const [address, setAddress] = useState(investor?.address ?? '');
  const [note, setNote] = useState(investor?.note ?? '');
  const [isActive, setIsActive] = useState(investor?.isActive ?? true);
  const pending = create.isPending || update.isPending;
  const error = investor ? update.error : create.error;
  const fields = error instanceof ApiError ? error.fields : undefined;

  return (
    <Modal open onClose={onClose} title={investor ? `Edit ${investor.name}` : 'Add an investor'}>
      <form
        aria-label={investor ? 'Edit investor' : 'Add an investor'}
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (investor) {
            // Empty clears a field; every field is sent, so the form is what is saved.
            const blank = (v: string) => (v.trim() ? v.trim() : null);
            update.mutate(
              {
                id: investor.id,
                input: {
                  name: name.trim(),
                  reference: blank(reference),
                  phone: blank(phone),
                  email: blank(email),
                  address: blank(address),
                  note: blank(note),
                  isActive,
                },
              },
              { onSuccess: () => (onSaved ?? onClose)() },
            );
          } else {
            create.mutate(
              {
                name,
                ...(reference ? { reference } : {}),
                ...(phone ? { phone } : {}),
                ...(email ? { email } : {}),
                ...(address ? { address } : {}),
                ...(note ? { note } : {}),
              },
              { onSuccess: onClose },
            );
          }
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} error={fields?.name?.[0]} />
          <Input
            label="Investor ID"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            hint="Your own reference, unique to each investor"
            error={fields?.reference?.[0]}
          />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} error={fields?.phone?.[0]} />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fields?.email?.[0]} />
        </div>
        <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} error={fields?.address?.[0]} />
        <Textarea label="Notes" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        {investor ? (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="size-4" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active — can be chosen as the owner of a bike
          </label>
        ) : null}
        {investor ? (
          <p className="text-xs text-slate-500">
            Profile details only. Bikes, agreements, collections, payouts and balances are not changed.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error instanceof ApiError ? error.message : 'Could not save. Your changes are still here — try again.'}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" loading={pending} disabled={pending || !name.trim()}>
            {investor ? (pending ? 'Saving…' : 'Save changes') : 'Add investor'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function PayoutForm({
  investorId,
  investmentId,
  payable,
  currency,
  onDone,
  onSaved,
}: {
  investorId: string;
  /** The vehicle partnership this payout settles; needed when the investor has several. */
  investmentId?: string;
  payable: string;
  currency: string;
  onDone: () => void;
  /** After a payout is recorded; `onDone` when not given. */
  onSaved?: () => void;
}): ReactNode {
  const { business } = useAuth();
  const timezone = business?.timezone ?? 'Africa/Lagos';
  const record = useRecordInvestorPayout();
  const [amount, setAmount] = useState(payable);
  const [paidOn, setPaidOn] = useState(todayIn(timezone));
  const [method, setMethod] = useState<(typeof METHODS)[number]>('bank_transfer');
  const [accountId, setAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [key] = useState(() => `payout-${crypto.randomUUID()}`);
  const fields = record.error instanceof ApiError ? record.error.fields : undefined;
  const duplicate = Boolean(fields?.confirmDuplicate);

  const submit = (confirmDuplicate: boolean) =>
    record.mutate(
      {
        investorId,
        input: {
          ...(investmentId ? { investmentId } : {}),
          amount,
          paidOn,
          method,
          ...(accountId ? { accountId } : {}),
          ...(reference ? { reference } : {}),
          ...(note ? { note } : {}),
          idempotencyKey: confirmDuplicate ? `${key}-confirmed` : key,
          ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
        },
      },
      { onSuccess: onSaved ?? onDone },
    );

  return (
    <form
      aria-label="Record payment to investor"
      className="mb-3 space-y-3 rounded-lg bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Amount"
          required
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={`Owed now: ${money(payable, currency)}`}
          error={fields?.amount?.[0]}
        />
        <Input
          label="Paid on"
          type="date"
          required
          value={paidOn}
          max={todayIn(timezone)}
          onChange={(e) => setPaidOn(e.target.value)}
          error={fields?.paidOn?.[0]}
        />
        <Select
          label="Method"
          value={method}
          onChange={(e) => {
            setMethod(e.target.value as (typeof METHODS)[number]);
            setAccountId('');
          }}
          options={METHODS.map((value) => ({ value, label: PAYMENT_METHOD_LABELS[value] ?? value }))}
        />
        <Input
          label="Transaction reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          hint="Bank transfer or receipt number"
          error={fields?.reference?.[0]}
        />
      </div>
      <AccountSelect method={method} value={accountId} onChange={setAccountId} error={fields?.accountId?.[0]} />
      <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} hint="Optional" />
      {record.error && (!fields || duplicate) ? <ErrorState message={record.error.message} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        {duplicate ? (
          <Button type="button" size="sm" onClick={() => submit(true)} disabled={record.isPending}>
            It is a separate payment — record it
          </Button>
        ) : (
          <Button type="submit" size="sm" disabled={record.isPending || !amount}>
            Record payment
          </Button>
        )}
      </div>
    </form>
  );
}
